import React from 'react';
import { Box, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTheme } from '@mui/material/styles';

const StatsCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = 'primary',
  trend,
  trendValue,
  onClick,
  variant = 'default', // 'default' | 'gradient' | 'outlined'
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const colorMap = {
    primary: {
      bg: isDark
        ? 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)'
        : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
      light: isDark ? 'rgba(99, 102, 241, 0.15)' : 'rgba(25, 118, 210, 0.1)',
      text: isDark ? '#818cf8' : '#1976d2',
    },
    success: {
      bg: isDark
        ? 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
        : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
      light: isDark ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.1)',
      text: '#2563eb',
    },
    warning: {
      bg: isDark
        ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
        : 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
      light: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
      text: '#f59e0b',
    },
    danger: {
      bg: isDark
        ? 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)'
        : 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
      light: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
      text: '#ef4444',
    },
    info: {
      bg: isDark
        ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
        : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
      light: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
      text: '#3b82f6',
    },
  };

  const colors = colorMap[color] || colorMap.primary;

  if (variant === 'gradient') {
    return (
      <Box
        onClick={onClick}
        sx={{
          background: colors.bg,
          borderRadius: '12px',
          p: { xs: 2, sm: 2.5, md: 3 },
          color: '#fff',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': onClick
            ? {
                transform: 'translateY(-4px)',
                boxShadow: isDark
                  ? '0 12px 24px rgba(0,0,0,0.5)'
                  : '0 12px 24px rgba(0,0,0,0.15)',
              }
            : {},
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: Icon ? 1 : 0 }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                opacity: 0.9,
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  opacity: 0.85,
                  mt: 1,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {Icon && (
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '12px',
                p: { xs: 1, sm: 1.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: { xs: 22, sm: 28 } }} />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
        borderRadius: '12px',
        p: { xs: 2, sm: 2.5, md: 3 },
        boxShadow: isDark
          ? '0 1px 3px rgba(0,0,0,0.5)'
          : '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: isDark
                ? '0 8px 24px rgba(0,0,0,0.4)'
                : '0 8px 24px rgba(0,0,0,0.12)',
            }
          : {},
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0, pr: Icon ? 1 : 0 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: isDark ? '#cbd5e1' : '#718096',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              fontWeight: 700,
              color: isDark ? '#ffffff' : '#1a202c',
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
          {(trend || subtitle) && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
              {trend && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: '6px',
                    backgroundColor: trend === 'up'
                      ? (isDark ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.1)')
                      : (isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)'),
                    color: trend === 'up' ? '#2563eb' : '#ef4444',
                    flexShrink: 0,
                  }}
                >
                  {trend === 'up' ? (
                    <TrendingUpIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 16 }} />
                  )}
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {trendValue}
                  </Typography>
                </Box>
              )}
              {subtitle && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: isDark ? '#718096' : '#718096',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {Icon && (
          <Box
            sx={{
              backgroundColor: colors.light,
              borderRadius: '12px',
              p: { xs: 1, sm: 1.5 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: { xs: 22, sm: 28 }, color: colors.text }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StatsCard;
